import * as React from 'react';
import { IBaseComponentProps } from '../../types';
import { TagInputField } from './tag-input-field';
import { TagContent } from './tag-input-tag-content';
import { ITagInputOption } from '../tag-input-types';
import { stopEventPropagation } from './utils';

export interface ITagProps extends Pick<IBaseComponentProps, 'className'> {
    item: ITagInputOption;
    disabled?: boolean;
    isStarredItem: boolean;
    showAddButton?: boolean;
    rootElementWidth: number;
    delimiter?: string;
    isInEditMode?: boolean;
    editorFieldValue: string;
    isFocused: boolean;
    listViewRef: React.RefObject<HTMLElement>;
    preventTriggerChangeOnBlur: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    onAddItem(): void;
    onRemove(item: ITagInputOption): void;
    onUpdate(item: ITagInputOption, value: string): void;
    onEdit(item: ITagInputOption): void;
    onChange(value: string): void;
    onKeyDown(ev: React.KeyboardEvent): void;
}

export class Tag extends React.PureComponent<ITagProps> {
    render() {
        const { isInEditMode } = this.props;
        return isInEditMode ? this.renderInputField() : this.renderTagContent();
    }

    private renderInputField = () => {
        const {
            editorFieldValue: inputFieldValue, showAddButton,
            rootElementWidth, onAddItem, disabled, inputRef
        } = this.props;
        return (
            <TagInputField
                inputFieldValue={inputFieldValue}
                inputRef={inputRef}
                rootElementWidth={rootElementWidth}
                onAddItem={onAddItem}
                showAddButton={showAddButton}
                onChange={this._onChange}
                disabled={disabled}
                onBlur={this._onBlur}
                onKeyDown={this._onKeyDown}
                autoFocus
                onClick={stopEventPropagation} />
        );
    }

    private renderTagContent = () => {
        const { item, isStarredItem, className, disabled } = this.props;
        return (
            <TagContent disabled={disabled}
                className={className}
                isStarredItem={isStarredItem}
                item={item}
                onEditItem={this.onEditItem}
                onRemoveItem={this.removeItem} />
        );
    }

    private onEditItem = () => {
        const { item, onEdit } = this.props;
        onEdit(item);
    }

    private _onBlur = (ev: React.FocusEvent) => {
        const { editorFieldValue: inputFieldValue, listViewRef, preventTriggerChangeOnBlur } = this.props;
        const { current } = listViewRef;
        stopEventPropagation(ev);

        if (preventTriggerChangeOnBlur && !ev.currentTarget.contains(current)) {
            if (!inputFieldValue) {
                this.removeItem();
            }
            return;
        }

        this.updateValue(inputFieldValue);
    }

    private _onKeyDown = (ev: React.KeyboardEvent) => {
        const { editorFieldValue: inputFieldValue, onKeyDown } = this.props;
        if (ev.keyCode === 13 || ev.keyCode === 9) {
            this.updateValue(inputFieldValue);
            return;
        }

        if (ev.keyCode === 8 && inputFieldValue === '') {
            this.removeItem();
            return;
        }

        onKeyDown(ev);
    }

    private updateValue = (newValue: string) => {
        const { onUpdate, item } = this.props;
        if (newValue) {
            onUpdate(item, newValue);
            return;
        }
        this.removeItem();
    }

    private _onChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
        const { delimiter = ',', onChange } = this.props;
        if (value.includes(delimiter)) {
            this.updateValue(value);
            return;
        }
        onChange(value);
    }

    private removeItem = () => {
        const { onRemove, item } = this.props;
        onRemove(item);
    }
}
